import { ReactElement, useState } from 'react'

import Dropdown from 'components/v2/Dropdown'
import { DotsIcon } from 'components/Icons/DotsIcon'
import IconButton from 'components/v2/IconButton'
import List from 'components/v2/List'
import { QrCodeIcon } from 'components/Icons/QrIcon'
import { DownloadPdfLink } from 'features/BusinessCustomer/components/DownloadPdf'
import { GTBusinessCustomer } from 'gql/types.generated'
import { mapBusinessCustomerUserAccountToPdf } from 'features/BusinessCustomer/utils'
import { FormattedMessage } from 'react-intl'
import { SwitchIcon } from 'components/Icons'
import { ActivateAction } from 'components/ActivateAction'
import { BulkDeactivationModal } from 'features/BusinessCustomer/components/BulkDeactivationModal'
import { CoinIcon } from 'components/Icons/CoinIcon'
import { BulkAssignKreditsModal } from 'features/BusinessCustomer/components/BulkAssignKreditsModal'
import { getQueries } from 'utils/route'
import { isEmpty } from 'lodash'
import { useQuery } from '@apollo/client'
import { GET_BUSINESS_CLIENT_KREDITS_SETTINGS } from 'features/Kredits/queries-legacy'
import { canWrite } from 'features/Users/utils'
import { FEATURES } from 'features/Roles/components/Permissions/constants'
import { useLogin } from 'features/Login/hooks'
import { GET_BUSINESS_SITE_KREDITS_SETTINGS } from 'features/Kredits/queries'

interface BusinessCustomerMultipleActionsProps {
  selectedItems: GTBusinessCustomer[]
}
export const BusinessCustomerMultipleActions = ({
  selectedItems
}: BusinessCustomerMultipleActionsProps): ReactElement | null => {
  const login = useLogin()
  const { clientId = '', siteId = '' } = getQueries(location.search)
  const { data: businessClientKreditsSettingsData } = useQuery(GET_BUSINESS_CLIENT_KREDITS_SETTINGS, {
    variables: { id: clientId },
    skip: isEmpty(clientId)
  })
  const { data: businessSiteKreditsSettingsData } = useQuery(GET_BUSINESS_SITE_KREDITS_SETTINGS, {
    variables: { id: siteId },
    skip: isEmpty(siteId)
  })
  const [isOpenDropdown, setIsOpenDropdown] = useState(false)
  const [isOpenBulkDeactivationModal, setIsOpenBulkDeactivationModal] = useState(false)
  const [isOpenBulkAssignKreditsModal, setIsOpenBulkAssignKreditsModal] = useState(false)
  const toggleDropdown = (): void => {
    setIsOpenDropdown(!isOpenDropdown)
  }

  const isDownloadButtonDisabled = selectedItems.length === 0
  const isDeactivateButtonDisabled = !selectedItems.some(({ isEnabled }) => isEnabled)
  const isAssignKreditsButtonDisabled =
    !canWrite(FEATURES.CORPORATE_KREDITS, login.accessToken.policies) ||
    selectedItems.length === 0 ||
    (isEmpty(businessClientKreditsSettingsData?.businessClient.kreditsSettings) &&
      isEmpty(businessSiteKreditsSettingsData?.businessSite.kreditsSettings))

  return (
    <>
      <Dropdown.Wrapper noPadding noIndicator right onClickAway={(): void => setIsOpenDropdown(false)}>
        <IconButton onClick={toggleDropdown} icon={<DotsIcon />} noBorder smallPadding />
        <Dropdown.Content open={isOpenDropdown} noIndicator left noOverflow>
          <List.Container>
            <List.Item action disabled={isDownloadButtonDisabled}>
              <List.Icon>
                <QrCodeIcon />
              </List.Icon>
              {isDownloadButtonDisabled ? (
                <FormattedMessage id="businessCustomer.downloadPdf" defaultMessage="Download QR" />
              ) : (
                <DownloadPdfLink
                  data={selectedItems
                    .filter(({ isEnabled }) => isEnabled)
                    .flatMap(({ userAccount, qrCode }) => ({ userAccount, qrCode }))
                    .map(({ userAccount, qrCode }) => {
                      return mapBusinessCustomerUserAccountToPdf(qrCode, userAccount)
                    })}
                />
              )}
            </List.Item>
            <List.Item
              action
              onClick={(): void => setIsOpenBulkDeactivationModal(true)}
              disabled={isDeactivateButtonDisabled}
            >
              <List.Icon>
                <SwitchIcon />
              </List.Icon>
              <ActivateAction isActive={true} />
            </List.Item>
            <List.Item
              action
              onClick={(): void => {
                if (isAssignKreditsButtonDisabled) {
                  return
                }

                setIsOpenBulkAssignKreditsModal(true)
              }}
              disabled={isAssignKreditsButtonDisabled}
            >
              <List.Icon>
                <CoinIcon />
              </List.Icon>
              <FormattedMessage id="businessCustomer.assignKredits" defaultMessage="Edit customer spread" />
            </List.Item>
          </List.Container>
        </Dropdown.Content>
      </Dropdown.Wrapper>
      {isOpenBulkDeactivationModal && (
        <BulkDeactivationModal
          selectedCustomers={selectedItems.filter(({ isEnabled }) => isEnabled)}
          closeModal={(): void => setIsOpenBulkDeactivationModal(false)}
          onSuccess={(): void => setIsOpenBulkDeactivationModal(false)}
        />
      )}
      {isOpenBulkAssignKreditsModal && (
        <BulkAssignKreditsModal
          businessClientId={clientId}
          selectedCustomers={selectedItems.filter(({ isEnabled }) => isEnabled)}
          closeModal={(): void => setIsOpenBulkAssignKreditsModal(false)}
          onSuccess={(): void => setIsOpenBulkAssignKreditsModal(false)}
        />
      )}
    </>
  )
}
